/**
 * CityLandingPage.jsx
 *
 * SEO landing page for city-specific searches.
 * Route: /tours-in/:citySlug  (e.g. /tours-in-austin-tx)
 * Provides H1, description, keyword content for non-JS crawlers via Helmet.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Star, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Map slug → display name
const CITY_SLUG_MAP = {
  'austin-tx': { city: 'Austin', state: 'TX', stateFullName: 'Texas' },
  'miami-fl': { city: 'Miami', state: 'FL', stateFullName: 'Florida' },
  'new-york-city-ny': { city: 'New York City', state: 'NY', stateFullName: 'New York' },
  'los-angeles-ca': { city: 'Los Angeles', state: 'CA', stateFullName: 'California' },
  'chicago-il': { city: 'Chicago', state: 'IL', stateFullName: 'Illinois' },
  'denver-co': { city: 'Denver', state: 'CO', stateFullName: 'Colorado' },
  'nashville-tn': { city: 'Nashville', state: 'TN', stateFullName: 'Tennessee' },
  'key-west-fl': { city: 'Key West', state: 'FL', stateFullName: 'Florida' },
  'san-francisco-ca': { city: 'San Francisco', state: 'CA', stateFullName: 'California' },
  'seattle-wa': { city: 'Seattle', state: 'WA', stateFullName: 'Washington' },
  'new-orleans-la': { city: 'New Orleans', state: 'LA', stateFullName: 'Louisiana' },
  'orlando-fl': { city: 'Orlando', state: 'FL', stateFullName: 'Florida' },
  'las-vegas-nv': { city: 'Las Vegas', state: 'NV', stateFullName: 'Nevada' },
  'san-diego-ca': { city: 'San Diego', state: 'CA', stateFullName: 'California' },
  'boston-ma': { city: 'Boston', state: 'MA', stateFullName: 'Massachusetts' },
  'honolulu-hi': { city: 'Honolulu', state: 'HI', stateFullName: 'Hawaii' },
  'scottsdale-az': { city: 'Scottsdale', state: 'AZ', stateFullName: 'Arizona' },
  'asheville-nc': { city: 'Asheville', state: 'NC', stateFullName: 'North Carolina' },
  'charleston-sc': { city: 'Charleston', state: 'SC', stateFullName: 'South Carolina' },
  'savannah-ga': { city: 'Savannah', state: 'GA', stateFullName: 'Georgia' },
  'sedona-az': { city: 'Sedona', state: 'AZ', stateFullName: 'Arizona' },
  'jackson-hole-wy': { city: 'Jackson Hole', state: 'WY', stateFullName: 'Wyoming' },
  'aspen-co': { city: 'Aspen', state: 'CO', stateFullName: 'Colorado' },
  'napa-ca': { city: 'Napa', state: 'CA', stateFullName: 'California' },
  'portland-or': { city: 'Portland', state: 'OR', stateFullName: 'Oregon' },
  'phoenix-az': { city: 'Phoenix', state: 'AZ', stateFullName: 'Arizona' },
  'houston-tx': { city: 'Houston', state: 'TX', stateFullName: 'Texas' },
  'dallas-tx': { city: 'Dallas', state: 'TX', stateFullName: 'Texas' },
  'san-antonio-tx': { city: 'San Antonio', state: 'TX', stateFullName: 'Texas' },
  'galveston-tx': { city: 'Galveston', state: 'TX', stateFullName: 'Texas' },
  'tampa-fl': { city: 'Tampa', state: 'FL', stateFullName: 'Florida' },
  'fort-lauderdale-fl': { city: 'Fort Lauderdale', state: 'FL', stateFullName: 'Florida' },
  'myrtle-beach-sc': { city: 'Myrtle Beach', state: 'SC', stateFullName: 'South Carolina' },
  'outer-banks-nc': { city: 'Outer Banks', state: 'NC', stateFullName: 'North Carolina' },
  'maui-hi': { city: 'Maui', state: 'HI', stateFullName: 'Hawaii' },
  'cape-cod-ma': { city: 'Cape Cod', state: 'MA', stateFullName: 'Massachusetts' },
  'boulder-co': { city: 'Boulder', state: 'CO', stateFullName: 'Colorado' },
  'minneapolis-mn': { city: 'Minneapolis', state: 'MN', stateFullName: 'Minnesota' },
  'atlanta-ga': { city: 'Atlanta', state: 'GA', stateFullName: 'Georgia' },
  'philadelphia-pa': { city: 'Philadelphia', state: 'PA', stateFullName: 'Pennsylvania' },
};

// Related cities to cross-link
const NEARBY_CITIES = {
  'austin-tx': ['houston-tx', 'dallas-tx', 'san-antonio-tx'],
  'miami-fl': ['key-west-fl', 'fort-lauderdale-fl', 'tampa-fl'],
  'new-york-city-ny': ['boston-ma', 'philadelphia-pa', 'cape-cod-ma'],
  'los-angeles-ca': ['san-diego-ca', 'san-francisco-ca', 'napa-ca'],
  'chicago-il': ['minneapolis-mn', 'milwaukee-wi', 'indianapolis-in'],
  'denver-co': ['boulder-co', 'aspen-co', 'jackson-hole-wy'],
  'nashville-tn': ['asheville-nc', 'savannah-ga', 'new-orleans-la'],
  'san-francisco-ca': ['napa-ca', 'los-angeles-ca', 'portland-or'],
};

const proxyImg = (url, w = 800) => {
  if (!url) return '';
  return `${API_URL}/api/v2/public/image-proxy?url=${encodeURIComponent(url)}&w=${w}`;
};

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  const cityData = CITY_SLUG_MAP[citySlug];

  useEffect(() => {
    if (!cityData) {
      navigate('/search', { replace: true });
      return;
    }
    // Fetch experiences for this city
    const url = `${API_URL}/api/v2/public/services?city=${encodeURIComponent(cityData.city)}&limit=9`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        setExperiences(data.services || data.results || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [citySlug, cityData, navigate]);

  if (!cityData) return null;

  const { city, state, stateFullName } = cityData;
  const pageTitle = `Things to Do in ${city}, ${state} | Tours, Activities & Experiences | BookingFlows`;
  const pageDesc = `Find and book the best tours, boat charters, outdoor adventures, and activities in ${city}, ${stateFullName}. Instant booking, secure payments, verified operators.`;
  const canonical = `https://bookingflows.com/tours-in/${citySlug}`;
  const relatedSlugs = NEARBY_CITIES[citySlug] || Object.keys(CITY_SLUG_MAP).filter(s => s !== citySlug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristDestination",
          "name": `${city}, ${stateFullName}`,
          "description": pageDesc,
          "url": canonical,
          "touristType": ["Adventure", "Cultural", "Nature"]
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="BookingFlows" className="h-8 w-8 rounded-xl" />
            <span className="text-xl font-bold text-gray-900">BookingFlows</span>
          </Link>
          <Button size="sm" onClick={() => navigate('/search')} variant="outline" className="rounded-full">
            All Experiences
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-blue-600 text-sm font-semibold mb-4">
            <MapPin className="w-4 h-4" />
            <span>{stateFullName}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Things to Do in {city}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Book tours, boat charters, outdoor adventures, and local experiences in {city}, {state}. Verified operators, instant confirmation, secure payments.
          </p>
          <Button
            onClick={() => navigate(`/search?city=${encodeURIComponent(city)}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base px-8 py-4 h-auto rounded-xl"
          >
            Browse All Experiences in {city} <ArrowRight className="ml-2 w-4 h-4 inline" />
          </Button>
        </div>
      </section>

      {/* Experience Cards */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
            Popular Experiences in {city}
          </h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-gray-200 animate-pulse h-64" />
              ))}
            </div>
          ) : experiences.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {experiences.map((exp) => (
                <Link
                  key={exp.id}
                  to={`/experience/${exp.id}`}
                  className="group rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    {exp.main_image_url && (
                      <img
                        src={proxyImg(exp.main_image_url, 400)}
                        alt={exp.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">{exp.name}</h3>
                    <div className="flex items-center gap-1 text-amber-500 text-sm mb-2">
                      <Star className="w-3.5 h-3.5 fill-current" />
                      <span>{exp.avg_rating?.toFixed(1) || '4.9'}</span>
                    </div>
                    {exp.price_from && (
                      <p className="text-gray-900 font-bold">From ${exp.price_from}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500 mb-4">No experiences found yet for {city}.</p>
              <Button onClick={() => navigate('/search')} variant="outline">
                Browse All Destinations
              </Button>
            </div>
          )}
          <div className="mt-8 text-center">
            <Link to={`/search?city=${encodeURIComponent(city)}`}>
              <Button variant="outline" className="rounded-xl">
                View all experiences in {city} <ChevronRight className="ml-1 w-4 h-4 inline" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About City - keyword rich content */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Discover {city}, {stateFullName}
          </h2>
          <div className="prose prose-gray max-w-none text-gray-600 space-y-4">
            <p>
              {city} is one of the most exciting destinations in {stateFullName}, offering a wide range of outdoor adventures, cultural experiences, boat charters, guided tours, and unique activities for every type of traveler. Whether you're planning a group outing, a romantic date, or a family vacation, {city} has something for everyone.
            </p>
            <p>
              BookingFlows makes it easy to find and book the best experiences in {city}. Browse verified operators offering tours, rentals, charters, and activities. Every booking comes with instant confirmation, a digital ticket, and secure Stripe payments — no phone calls, no waiting.
            </p>
            <p>
              Popular things to do in {city} include boat charters, kayaking and paddleboarding, guided city tours, outdoor adventures, wine tastings, food tours, museum visits, escape rooms, and much more. New experiences are added regularly, so check back often.
            </p>
          </div>
        </div>
      </section>

      {/* Related Cities */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Explore Nearby Destinations</h2>
          <div className="flex flex-wrap gap-3">
            {relatedSlugs.map(slug => {
              const relCity = CITY_SLUG_MAP[slug];
              if (!relCity) return null;
              return (
                <Link
                  key={slug}
                  to={`/tours-in/${slug}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:border-blue-400 hover:text-blue-600 text-sm transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {relCity.city}, {relCity.state}
                </Link>
              );
            })}
            <Link
              to="/search"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
            >
              All Destinations <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-3">Explore</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/search" className="hover:text-white transition-colors">All Experiences</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3">Software</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/for-suppliers" className="hover:text-white transition-colors">Booking Software</Link></li>
                <li><Link to="/for-tours" className="hover:text-white transition-colors">Tour Booking</Link></li>
                <li><Link to="/for-charters" className="hover:text-white transition-colors">Charter Booking</Link></li>
                <li><Link to="/for-rentals" className="hover:text-white transition-colors">Rental Booking</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3">Top Cities</h3>
              <ul className="space-y-2 text-sm">
                {Object.entries(CITY_SLUG_MAP).slice(0, 8).map(([slug, c]) => (
                  <li key={slug}>
                    <Link to={`/tours-in/${slug}`} className="hover:text-white transition-colors">
                      {c.city}, {c.state}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-3">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 text-sm">
            <p>© {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Export slug map for other uses
export { CITY_SLUG_MAP };
